/**
 * PublicHSEPage — mobile page for HSE officers to scan an employee badge and
 * view their training profile, then add welfare notes.
 * URL: https://<app>/?hse=1
 * No login required. Officer identity is verified by their own badge QR scan.
 * Uses raw fetch() — no Axios interceptor, keys stay snake_case from the API.
 */
import React, { useState, useRef, useEffect } from 'react';
import jsQR from 'jsqr';
import { ShieldCheck, QrCode, User, CheckCircle, ChevronLeft, X, Camera } from 'lucide-react';

const API = process.env.REACT_APP_API_URL || 'http://localhost:4000';

const S = {
  bg: '#0a0e17', surface: '#131a2b', elevated: '#1a2235',
  border: 'rgba(255,255,255,0.08)', text: '#e2e8f0', muted: '#64748b',
  subtle: '#94a3b8', green: '#22c55e', red: '#ef4444', blue: '#3b82f6',
  amber: '#f59e0b',
};

const STATUS_STYLE = {
  PASS:    { bg: S.green + '22', color: S.green,  label: 'PASS' },
  EXPIRED: { bg: S.red + '22',   color: S.red,    label: 'EXPIRED' },
  PENDING: { bg: S.amber + '22', color: S.amber,  label: 'PENDING' },
};

function extractHashFromQR(raw) {
  try {
    const url = new URL(raw);
    const hash = url.searchParams.get('e') || url.searchParams.get('vis') || url.searchParams.get('z');
    return (hash || raw).toUpperCase();
  } catch {
    return raw.toUpperCase();
  }
}

function QrScannerModal({ onScan, onClose }) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const rafRef = useRef(null);
  const doneRef = useRef(false);
  const [camError, setCamError] = useState('');

  useEffect(() => {
    async function start() {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } });
        if (doneRef.current) { stream.getTracks().forEach(t => t.stop()); return; }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.play().then(tick).catch(() => {});
        }
      } catch {
        setCamError('Camera access denied. Allow camera permission and try again.');
      }
    }

    function tick() {
      if (doneRef.current) return;
      const video = videoRef.current;
      const canvas = canvasRef.current;
      if (video && canvas && video.readyState === video.HAVE_ENOUGH_DATA) {
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        const img = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const code = jsQR(img.data, img.width, img.height);
        if (code?.data) {
          doneRef.current = true;
          stop();
          onScan(extractHashFromQR(code.data.trim()));
          return;
        }
      }
      rafRef.current = requestAnimationFrame(tick);
    }

    function stop() {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      streamRef.current?.getTracks().forEach(t => t.stop());
    }

    start();
    return () => { doneRef.current = true; stop(); };
  }, [onScan]);

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.95)', zIndex: 1000, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
      <div style={{ width: '100%', maxWidth: '360px' }}>
        <div style={{ textAlign: 'center', marginBottom: '14px', color: S.text, fontSize: '15px', fontWeight: 700 }}>
          Point camera at QR code
        </div>
        {camError ? (
          <div style={{ color: S.red, fontSize: '13px', textAlign: 'center', padding: '20px 0' }}>{camError}</div>
        ) : (
          <video
            ref={videoRef}
            playsInline
            muted
            style={{ width: '100%', borderRadius: '12px', display: 'block', background: S.surface }}
          />
        )}
        <canvas ref={canvasRef} style={{ display: 'none' }} />
        <button
          onClick={onClose}
          style={{ width: '100%', marginTop: '16px', background: S.elevated, border: `1px solid ${S.border}`, color: S.subtle, borderRadius: '8px', padding: '13px', fontSize: '14px', fontWeight: 600, cursor: 'pointer' }}
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

export default function PublicHSEPage() {
  // phase: officer | scan | loading | view | error
  const [phase, setPhase] = useState('officer');
  const [officer, setOfficer] = useState(null);  // { id, name }
  const [officerHash, setOfficerHash] = useState('');
  const [officerInput, setOfficerInput] = useState('');
  const [employeeInput, setEmployeeInput] = useState('');
  const [employee, setEmployee] = useState(null);
  const [error, setError] = useState('');
  const [noteText, setNoteText] = useState('');
  const [noteCourseId, setNoteCourseId] = useState('');
  const [savingNote, setSavingNote] = useState(false);
  const [notes, setNotes] = useState([]);
  const [noteSuccess, setNoteSuccess] = useState(false);
  const [scanner, setScanner] = useState(null); // 'officer' | 'employee' | null
  const [entityType, setEntityType] = useState('employee');
  const [visitorData, setVisitorData] = useState(null);

  async function verifyOfficer(hash) {
    setError('');
    try {
      const res = await fetch(`${API}/api/public/hse/officer/${hash}`);
      const data = await res.json();
      if (!res.ok || data.error) { setError(data.error || 'Verification failed'); return; }
      setOfficer(data);
      setOfficerHash(hash);
      setPhase('scan');
    } catch {
      setError('Unable to reach the server. Check your connection.');
    }
  }

  async function lookupEntity(hash) {
    setError('');
    setPhase('loading');
    try {
      const res = await fetch(`${API}/api/public/hse/scan/${hash}`);
      const data = await res.json();
      if (!res.ok || data.error) { setError(data.error || 'Not found'); setPhase('scan'); return; }
      if (data.type === 'employee') {
        setEntityType('employee');
        setEmployee(data);
        setVisitorData(null);
      } else {
        setEntityType('visitor');
        setVisitorData(data);
        setEmployee(null);
      }
      setNotes([]);
      setNoteText('');
      setNoteCourseId('');
      setNoteSuccess(false);
      setPhase('view');
    } catch {
      setError('Unable to reach the server. Check your connection.');
      setPhase('scan');
    }
  }

  function handleOfficerScan(e) {
    e.preventDefault();
    const hash = officerInput.trim().toUpperCase();
    if (!hash) return;
    verifyOfficer(hash);
  }

  function handleEmployeeScan(e) {
    e.preventDefault();
    const hash = employeeInput.trim().toUpperCase();
    if (!hash) return;
    lookupEntity(hash);
  }

  function handleCameraScan(decoded) {
    setScanner(null);
    if (scanner === 'officer') {
      setOfficerInput(decoded);
      verifyOfficer(decoded);
    } else if (scanner === 'employee') {
      setEmployeeInput(decoded);
      lookupEntity(decoded);
    }
  }

  async function handleAddNote(e) {
    e.preventDefault();
    if (!noteText.trim()) return;
    setSavingNote(true);
    setNoteSuccess(false);
    try {
      const res = await fetch(`${API}/api/public/hse/notes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          officerQrHash: officerHash,
          employeeQrHash: employeeInput.trim().toUpperCase(),
          courseId: noteCourseId || null,
          note: noteText.trim(),
        }),
      });
      const data = await res.json();
      if (!res.ok || data.error) { setError(data.error || 'Failed to save note'); return; }
      const courseName = employee.training.find(r => r.course_id === noteCourseId)?.course_name || null;
      setNotes(prev => [{ ...data, course_name: courseName }, ...prev]);
      setNoteText('');
      setNoteCourseId('');
      setNoteSuccess(true);
      setTimeout(() => setNoteSuccess(false), 3000);
    } finally {
      setSavingNote(false);
    }
  }

  const activeTraining = entityType === 'employee' ? (employee?.training ?? []) : (visitorData?.training ?? []);
  const passCount = activeTraining.filter(r => r.effective_status === 'PASS').length;
  const expiredCount = activeTraining.filter(r => r.effective_status === 'EXPIRED').length;
  const pendingCount = activeTraining.filter(r => r.effective_status === 'PENDING').length;

  return (
    <div style={{ minHeight: '100vh', background: S.bg, color: S.text, fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif', padding: '0 0 40px' }}>
      {/* Header */}
      <div style={{ background: S.surface, borderBottom: `1px solid ${S.border}`, padding: '14px 18px', display: 'flex', alignItems: 'center', gap: '10px' }}>
        {phase === 'view' && (
          <button onClick={() => { setPhase('scan'); setEmployee(null); setVisitorData(null); setEntityType('employee'); setEmployeeInput(''); setError(''); }}
            style={{ background: 'transparent', border: 'none', color: S.muted, cursor: 'pointer', padding: '0 8px 0 0', display: 'flex', alignItems: 'center' }}>
            <ChevronLeft size={18} />
          </button>
        )}
        <ShieldCheck size={20} color={S.blue} />
        <span style={{ fontWeight: 700, fontSize: '15px' }}>HSE Officer</span>
        {officer && (
          <span style={{ marginLeft: 'auto', fontSize: '12px', color: S.muted }}>{officer.name}</span>
        )}
      </div>

      <div style={{ padding: '22px 18px', maxWidth: '480px', margin: '0 auto' }}>

        {/* ── Phase: Officer scan ── */}
        {phase === 'officer' && (
          <>
            <div style={{ textAlign: 'center', marginBottom: '28px' }}>
              <QrCode size={48} color={S.blue} style={{ marginBottom: '12px' }} />
              <h2 style={{ color: S.text, fontSize: '17px', margin: '0 0 6px' }}>Identify Yourself</h2>
              <p style={{ color: S.muted, fontSize: '13px', margin: 0 }}>Scan or enter your badge QR code to verify your HSE officer identity.</p>
            </div>
            <form onSubmit={handleOfficerScan}>
              <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
                <input
                  value={officerInput}
                  onChange={e => setOfficerInput(e.target.value)}
                  placeholder="YOUR BADGE QR CODE"
                  autoFocus
                  style={{ flex: 1, background: S.elevated, border: `1px solid ${S.border}`, borderRadius: '8px', padding: '12px 14px', color: S.text, fontSize: '14px', boxSizing: 'border-box', textTransform: 'uppercase' }}
                />
                <button
                  type="button"
                  onClick={() => setScanner('officer')}
                  title="Scan with camera"
                  style={{ background: S.elevated, border: `1px solid ${S.border}`, borderRadius: '8px', padding: '0 14px', color: S.blue, cursor: 'pointer', display: 'flex', alignItems: 'center', flexShrink: 0 }}
                >
                  <Camera size={20} />
                </button>
              </div>
              {error && <div style={{ color: S.red, fontSize: '13px', marginBottom: '10px' }}>{error}</div>}
              <button type="submit" disabled={!officerInput.trim()}
                style={{ width: '100%', background: S.blue, color: '#fff', border: 'none', borderRadius: '8px', padding: '13px', fontSize: '15px', fontWeight: 700, cursor: 'pointer', opacity: !officerInput.trim() ? 0.5 : 1 }}>
                Verify Identity
              </button>
            </form>
          </>
        )}

        {/* ── Phase: Scan employee ── */}
        {(phase === 'scan' || phase === 'loading') && (
          <>
            <div style={{ background: S.elevated, border: `1px solid ${S.border}`, borderRadius: '8px', padding: '12px 14px', marginBottom: '22px', display: 'flex', alignItems: 'center', gap: '10px' }}>
              <CheckCircle size={16} color={S.green} />
              <span style={{ color: S.subtle, fontSize: '13px' }}>Verified as <strong style={{ color: S.text }}>{officer?.name}</strong></span>
            </div>
            <div style={{ textAlign: 'center', marginBottom: '24px' }}>
              <User size={40} color={S.blue} style={{ marginBottom: '10px' }} />
              <h2 style={{ color: S.text, fontSize: '17px', margin: '0 0 6px' }}>Scan Employee Badge</h2>
              <p style={{ color: S.muted, fontSize: '13px', margin: 0 }}>Scan or enter the employee's badge QR code.</p>
            </div>
            <form onSubmit={handleEmployeeScan}>
              <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
                <input
                  value={employeeInput}
                  onChange={e => setEmployeeInput(e.target.value)}
                  placeholder="EMPLOYEE BADGE QR CODE"
                  autoFocus
                  disabled={phase === 'loading'}
                  style={{ flex: 1, background: S.elevated, border: `1px solid ${S.border}`, borderRadius: '8px', padding: '12px 14px', color: S.text, fontSize: '14px', boxSizing: 'border-box', textTransform: 'uppercase' }}
                />
                <button
                  type="button"
                  onClick={() => setScanner('employee')}
                  disabled={phase === 'loading'}
                  title="Scan with camera"
                  style={{ background: S.elevated, border: `1px solid ${S.border}`, borderRadius: '8px', padding: '0 14px', color: S.blue, cursor: 'pointer', display: 'flex', alignItems: 'center', flexShrink: 0, opacity: phase === 'loading' ? 0.5 : 1 }}
                >
                  <Camera size={20} />
                </button>
              </div>
              {error && <div style={{ color: S.red, fontSize: '13px', marginBottom: '10px' }}>{error}</div>}
              <button type="submit" disabled={!employeeInput.trim() || phase === 'loading'}
                style={{ width: '100%', background: S.blue, color: '#fff', border: 'none', borderRadius: '8px', padding: '13px', fontSize: '15px', fontWeight: 700, cursor: 'pointer', opacity: (!employeeInput.trim() || phase === 'loading') ? 0.5 : 1 }}>
                {phase === 'loading' ? 'Looking up…' : 'Load Profile'}
              </button>
            </form>
          </>
        )}

        {/* ── Phase: View profile ── */}
        {phase === 'view' && (entityType === 'employee' ? employee : visitorData) && (
          <>
            {/* Employee / Visitor card */}
            <div style={{ background: S.elevated, border: `1px solid ${S.border}`, borderRadius: '10px', padding: '16px', marginBottom: '18px' }}>
              {entityType === 'visitor' && visitorData?.photoUrl && (
                <img
                  src={visitorData.photoUrl}
                  alt={visitorData.name}
                  style={{ width: '72px', height: '72px', objectFit: 'cover', borderRadius: '8px', border: '2px solid #4a9eff', marginBottom: '10px', display: 'block' }}
                />
              )}
              <div style={{ fontWeight: 700, fontSize: '16px', marginBottom: '3px' }}>{entityType === 'employee' ? employee.name : visitorData.name}</div>
              {entityType === 'employee' && employee.department && <div style={{ color: S.muted, fontSize: '12px', marginBottom: '8px' }}>{employee.department}</div>}
              {entityType === 'visitor' && visitorData.employer && <div style={{ color: S.muted, fontSize: '12px', marginBottom: '4px' }}>Employer: {visitorData.employer}</div>}
              {entityType === 'visitor' && visitorData.visitorId && (
                <div style={{ fontSize: '11px', color: '#4a9eff', marginBottom: '8px', fontFamily: 'monospace' }}>{visitorData.visitorId}</div>
              )}
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                <span style={{ background: S.green + '22', color: S.green, padding: '2px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: 600 }}>{passCount} passed</span>
                {expiredCount > 0 && <span style={{ background: S.red + '22', color: S.red, padding: '2px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: 600 }}>{expiredCount} expired</span>}
                {pendingCount > 0 && <span style={{ background: S.amber + '22', color: S.amber, padding: '2px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: 600 }}>{pendingCount} pending</span>}
              </div>
            </div>

            {/* Training records */}
            {activeTraining.length === 0 ? (
              <div style={{ color: S.muted, fontSize: '13px', textAlign: 'center', padding: '16px 0', marginBottom: '18px' }}>No trainings assigned.</div>
            ) : (
              <div style={{ marginBottom: '20px' }}>
                <div style={{ fontSize: '11px', color: S.muted, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '8px' }}>Training Records</div>
                {activeTraining.map(r => {
                  const s = STATUS_STYLE[r.effective_status] || STATUS_STYLE.PENDING;
                  return (
                    <div key={r.id} style={{ background: S.elevated, border: `1px solid ${S.border}`, borderRadius: '8px', padding: '12px 14px', marginBottom: '6px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div>
                        <div style={{ fontWeight: 600, fontSize: '13px', marginBottom: '3px' }}>{r.course_name}</div>
                        {r.expiry_date && (
                          <div style={{ fontSize: '11px', color: r.effective_status === 'EXPIRED' ? S.red : S.muted }}>
                            {r.effective_status === 'EXPIRED' ? '⚠ Expired ' : 'Expires '}{new Date(r.expiry_date).toLocaleDateString()}
                          </div>
                        )}
                      </div>
                      <span style={{ background: s.bg, color: s.color, padding: '3px 9px', borderRadius: '4px', fontSize: '10px', fontWeight: 700, flexShrink: 0 }}>{s.label}</span>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Add note — employees only */}
            {entityType === 'employee' && (
              <div style={{ borderTop: `1px solid ${S.border}`, paddingTop: '18px' }}>
                <div style={{ fontSize: '11px', color: S.muted, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '10px' }}>Add Note</div>
                <form onSubmit={handleAddNote} style={{ display: 'flex', flexDirection: 'column', gap: '9px' }}>
                  <select
                    value={noteCourseId}
                    onChange={e => setNoteCourseId(e.target.value)}
                    style={{ background: S.elevated, border: `1px solid ${S.border}`, borderRadius: '7px', padding: '10px 12px', color: S.text, fontSize: '13px' }}
                  >
                    <option value="">— General welfare note —</option>
                    {activeTraining.map(r => <option key={r.course_id} value={r.course_id}>{r.course_name}</option>)}
                  </select>
                  <textarea
                    value={noteText}
                    onChange={e => setNoteText(e.target.value)}
                    placeholder="Add a welfare or training note…"
                    rows={3}
                    style={{ background: S.elevated, border: `1px solid ${S.border}`, borderRadius: '7px', padding: '10px 12px', color: S.text, fontSize: '13px', resize: 'vertical' }}
                  />
                  {error && <div style={{ color: S.red, fontSize: '12px' }}>{error}</div>}
                  {noteSuccess && <div style={{ color: S.green, fontSize: '12px' }}>Note saved.</div>}
                  <button type="submit" disabled={savingNote || !noteText.trim()}
                    style={{ background: S.blue, color: '#fff', border: 'none', borderRadius: '8px', padding: '12px', fontSize: '14px', fontWeight: 700, cursor: 'pointer', opacity: (savingNote || !noteText.trim()) ? 0.5 : 1 }}>
                    {savingNote ? 'Saving…' : 'Save Note'}
                  </button>
                </form>

                {/* Newly added notes this session */}
                {notes.length > 0 && (
                  <div style={{ marginTop: '16px' }}>
                    <div style={{ fontSize: '11px', color: S.muted, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '8px' }}>Added This Session</div>
                    {notes.map((n, i) => (
                      <div key={i} style={{ background: S.elevated, border: `1px solid ${S.border}`, borderRadius: '8px', padding: '12px 14px', marginBottom: '6px' }}>
                        <div style={{ fontSize: '12px', color: S.subtle, marginBottom: '5px' }}>{n.note}</div>
                        {n.course_name
                          ? <span style={{ background: S.amber + '22', color: S.amber, padding: '1px 7px', borderRadius: '4px', fontSize: '10px', fontWeight: 600 }}>{n.course_name}</span>
                          : <span style={{ background: S.border, color: S.muted, padding: '1px 7px', borderRadius: '4px', fontSize: '10px' }}>General</span>
                        }
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Scan another */}
            <div style={{ borderTop: entityType === 'visitor' ? `1px solid ${S.border}` : 'none', paddingTop: entityType === 'visitor' ? '18px' : '0', marginTop: entityType === 'visitor' ? '18px' : '0' }}>
              <button
                onClick={() => { setPhase('scan'); setEmployee(null); setVisitorData(null); setEntityType('employee'); setEmployeeInput(''); setError(''); setNotes([]); }}
                style={{ width: '100%', marginTop: '18px', background: S.surface, border: `1px solid ${S.border}`, color: S.subtle, borderRadius: '8px', padding: '12px', fontSize: '13px', fontWeight: 600, cursor: 'pointer' }}
              >
                Scan Another
              </button>
            </div>
          </>
        )}
      </div>

      {/* Camera scanner overlay */}
      {scanner && (
        <QrScannerModal
          onScan={handleCameraScan}
          onClose={() => setScanner(null)}
        />
      )}
    </div>
  );
}
